import { Component, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OdontogramaConfigService } from "app/services/odontograma-config.service";

interface FilaCondicion {
  codigo: string | null;
  nombre: string;
  color: string;
  oculto: boolean;
  sistema: boolean;
}

const COLOR_DEFECTO = "#999999";

@Component({
  selector: "app-odontograma-config",
  templateUrl: "./odontograma-config.component.html",
  styleUrls: ["./odontograma-config.component.scss"],
})
export class OdontogramaConfigComponent implements OnInit {
  porCara: FilaCondicion[] = [];
  porDiente: FilaCondicion[] = [];
  guardando = false;

  constructor(
    private configService: OdontogramaConfigService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.configService.cargar().subscribe((cfg) => {
      if (!cfg) return;
      this.porCara = this.configService.cara.map((c) => ({ ...c, nombre: c.label }));
      this.porDiente = this.configService.diente.map((c) => ({ ...c, nombre: c.label }));
    });
  }

  agregar(grupo: "porCara" | "porDiente"): void {
    this[grupo].push({ codigo: null, nombre: "", color: COLOR_DEFECTO, oculto: false, sistema: false });
  }

  eliminar(grupo: "porCara" | "porDiente", c: FilaCondicion): void {
    if (!c.codigo) {
      const i = this[grupo].indexOf(c);
      if (i >= 0) this[grupo].splice(i, 1);
    } else {
      c.oculto = true;
    }
  }

  guardar(): void {
    if (!this.validar(this.porCara) || !this.validar(this.porDiente)) {
      this.snackBar.open("Revisá los nombres: son requeridos y no pueden repetirse.", "OK", { duration: 4000 });
      return;
    }

    const asignaciones: { fila: FilaCondicion; codigo: string }[] = [];
    const porCara = this.aMapa(this.porCara, asignaciones);
    const porDiente = this.aMapa(this.porDiente, asignaciones);

    this.guardando = true;
    this.configService.guardar({ porCara, porDiente }).subscribe({
      next: () => {
        this.guardando = false;
        asignaciones.forEach((a) => (a.fila.codigo = a.codigo));
        this.snackBar.open("Configuración guardada", "OK", { duration: 3000 });
      },
      error: () => {
        this.guardando = false;
        this.snackBar.open("Error al guardar", "OK", { duration: 4000 });
      },
    });
  }

  private validar(filas: FilaCondicion[]): boolean {
    const nombres = filas.map((f) => f.nombre.trim().toLowerCase());
    if (nombres.some((n) => n.length === 0)) return false;
    return new Set(nombres).size === nombres.length;
  }

  private aMapa(
    filas: FilaCondicion[],
    asignaciones: { fila: FilaCondicion; codigo: string }[],
  ): Record<string, { nombre: string; color: string; oculto: boolean }> {
    const usados = new Set<string>(filas.filter((f) => f.codigo).map((f) => f.codigo as string));
    const mapa: Record<string, { nombre: string; color: string; oculto: boolean }> = {};
    for (const f of filas) {
      let codigo = f.codigo;
      if (!codigo) {
        codigo = this.codigoUnico(this.slug(f.nombre), usados);
        asignaciones.push({ fila: f, codigo });
      }
      mapa[codigo] = { nombre: f.nombre.trim(), color: f.color, oculto: f.oculto };
    }
    return mapa;
  }

  private slug(nombre: string): string {
    const base = nombre
      .toLowerCase()
      .replace(/[áàä]/g, "a")
      .replace(/[éèë]/g, "e")
      .replace(/[íìï]/g, "i")
      .replace(/[óòö]/g, "o")
      .replace(/[úùü]/g, "u")
      .replace(/ñ/g, "n")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return base || "condicion";
  }

  private codigoUnico(base: string, usados: Set<string>): string {
    let codigo = base;
    let i = 2;
    while (usados.has(codigo)) {
      codigo = `${base}_${i++}`;
    }
    usados.add(codigo);
    return codigo;
  }
}
